import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  TablePagination,
  IconButton
} from '@mui/material';
import { Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import api from '../api/api';

const AuditoriaConexiones = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Estados de Paginación del Servidor
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // Material UI cuenta las páginas desde 0
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchEmail, setSearchEmail] = useState('');
  
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [error, setError] = useState(false);

  // Función para consultar al backend con los parámetros dinámicos
  const fetchAuditorias = useCallback(async (pageTarget, sizeTarget, emailTarget) => {
    setLoading(true);
    try {
      // El backend recibe la página basándose en 1, React maneja base 0
      const paginaBackend = pageTarget + 1;
      
      let url = `/auditoria-conexiones/?page=${paginaBackend}&size=${sizeTarget}`;
      if (emailTarget) {
        url += `&email=${encodeURIComponent(emailTarget)}`;
      }

      const response = await api.get(url);
      
      // La respuesta ahora viene envuelta en { items, total, page, size }
      setLogs(response.data.items || []);
      setTotal(response.data.total || 0);
      setError(false);
    } catch (err) {
      console.error('Error al obtener auditorías:', err);
      setError(true);
      setSnackbarMessage('Error al cargar el historial de auditoría desde el servidor');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Efecto que reacciona al cambio de página o cantidad de filas
  useEffect(() => {
    fetchAuditorias(page, rowsPerPage, searchEmail);
  }, [page, rowsPerPage, fetchAuditorias]);

  // Manejador del cambio de página de Material UI
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Manejador del cambio de tamaño de registros por página
  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0); // Reiniciar siempre a la primera página
  };

  // Buscar cuando el usuario presiona Enter o saca el foco del input
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setPage(0); // Reiniciar a la primera página para la nueva búsqueda
      fetchAuditorias(0, rowsPerPage, searchEmail);
    }
  };

  const handleClearSearch = () => {
    setSearchEmail('');
    setPage(0);
    fetchAuditorias(0, rowsPerPage, '');
  };

  const formatFecha = (fechaInput) => {
    if (!fechaInput) return 'N/D';
    const date = new Date(fechaInput);
    return date.toLocaleString('es-SV', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      <AppBar position="static" sx={{ bgcolor: '#fff', color: '#000', boxShadow: 1 }}>
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            Auditoría de Conexiones
          </Typography>
          <TextField
            size="small"
            placeholder="Buscar correo (Presiona Enter)..."
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ width: { xs: '100%', sm: '350px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchEmail && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', TypographyalignItems: 'center', height: '50vh' }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : (
          <Paper sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table sx={{ minWidth: 650 }} aria-label="tabla de auditoria">
                <TableHead sx={{ bgcolor: '#1976d2' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>ID Log</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Usuario ID</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Correo Ingresado</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Dirección IP</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Estado</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Detalle / Motivo</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Navegador / Entorno</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Fecha y Hora</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                        No se encontraron registros de conexiones.
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id_auditoria} hover>
                        <TableCell sx={{ fontWeight: 'bold' }}>{log.id_auditoria}</TableCell>
                        <TableCell>{log.id_usuario ? log.id_usuario : <Chip label="Ninguno" size="small" variant="outlined" />}</TableCell>
                        <TableCell>{log.email_ingresado}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{log.ip_address}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.estado}
                            size="small"
                            color={log.estado === 'EXITOSO' ? 'success' : 'error'}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: log.estado === 'FALLIDO' ? 'error.main' : 'text.primary' }}>
                          {log.motivo_fallo ? log.motivo_fallo : 'Acceso concedido'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.user_agent || 'Desconocido'} arrow>
                            <Typography variant="body2" noWrap sx={{ maxWidth: '180px', cursor: 'pointer', color: 'text.secondary' }}>
                              {log.user_agent || 'Desconocido'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFecha(log.createdAt)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Componente de Paginación Oficial de Material UI */}
            <TablePagination
              rowsPerPageOptions={[10, 15, 30, 50]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Registros por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
            />
          </Paper>
        )}
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1500 }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AuditoriaConexiones;